import type { OnChanges } from '@angular/core';
import {
  Component,
  DestroyRef,
  Injector,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, NgControl } from '@angular/forms';
import type { ValidationError } from '@angular/forms/signals';
import { FormField } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { castArray, isString } from 'lodash';
import type { Observable } from 'rxjs';
import { Subject, takeUntil } from 'rxjs';
import type { OnyxMaybeArray } from '../../../interfaces';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxDateFormat, OnyxDatePipe } from '../../../pipes/onyx-date.pipe';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import type { OnyxError } from '../interfaces';

type OnyxErrorType = OnyxMaybeArray<
  NgControl | AbstractControl | FormField<unknown> | OnyxError
>;

@Component({
  selector: 'onyx-error',
  imports: [TranslatePipe, OnyxIconComponent],
  providers: [
    {
      provide: OnyxDatePipe,
      useClass: OnyxDatePipe,
    },
  ],
  templateUrl: './onyx-error.component.html',
  styleUrl: './onyx-error.component.scss',
})
export class OnyxErrorComponent implements OnChanges {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly onyxDatePipe = inject(OnyxDatePipe);

  public readonly errors = input.required<OnyxErrorType>();
  public readonly variant = input<'control' | 'standard' | 'small'>('standard');
  public readonly align = input<'left' | 'right'>('left');

  public readonly hasErrorChange = output<boolean>();

  protected readonly I18N = `${I18N_NAMESPACE}.formControlErrors`;

  protected readonly isString = isString;

  protected error = signal<OnyxError | null>(null);
  private next$ = new Subject<void>();

  public ngOnChanges(): void {
    const controls = this.errors();

    if (this.isError(controls)) {
      this.error.set(controls);
      this.hasErrorChange.emit(true);
      return;
    }

    effect(
      () => {
        this.next$.next();
        this.update();

        for (const control of castArray(this.errors())) {
          if (this.isError(control) || control instanceof FormField) continue;

          const statusChanges$ = (
            control.statusChanges as Observable<any> | undefined
          )?.pipe(takeUntil(this.next$), takeUntilDestroyed(this.destroyRef));
          const valueChanges$ = control.valueChanges?.pipe(
            takeUntil(this.next$),
            takeUntilDestroyed(this.destroyRef),
          );

          statusChanges$?.subscribe(() => this.update());
          valueChanges$?.subscribe(() => this.update());
        }
      },
      { injector: this.injector },
    );
  }

  private update(): void {
    const error = this.getError();
    this.error.set(error);
    this.hasErrorChange.emit(!!error);
  }

  private getError(): OnyxError | null {
    for (const control of castArray(this.errors())) {
      if (this.isError(control)) continue;

      if (control instanceof FormField) {
        const errors = control.errors();
        const state = control.state();
        const isDirty = state.dirty() && state.touched();
        if (!errors.length || !isDirty) continue;

        const error = errors[0];
        return this.transformError(
          { minLength: 'minlength', maxLength: 'maxlength' }[error.kind] ??
            error.kind,
          this.getErrorData(error),
        );
      }

      if (control instanceof AbstractControl || control instanceof NgControl) {
        const errors = control.errors;
        const isDirty = control.dirty && control.touched;
        if (!errors || !isDirty) continue;

        const [errorCode, errorValue] = Object.entries(errors)[0];
        return this.transformError(errorCode, errorValue);
      }
    }

    return null;
  }

  private getErrorData(error: ValidationError): Record<string, unknown> {
    const extendedError = error as ValidationError & {
      minLength?: number;
      maxLength?: number;
    };

    return {
      ...extendedError,
      requiredLength: extendedError.minLength ?? extendedError.maxLength,
    };
  }

  private transformError(code: string, data: any): OnyxError {
    if (code === 'min' && data.min === Number.EPSILON) {
      return { code: 'minEpsilon' };
    } else if (code === 'required' && this.variant() !== 'control') {
      return { code: 'requiredTrue' };
    }

    const transformDate = (date: string) =>
      this.onyxDatePipe.transform(date, OnyxDateFormat.DATE_DOT);

    const value =
      {
        min: data.min,
        max: data.max,
        minlength: data.requiredLength,
        maxlength: data.requiredLength,
        minDate: transformDate(data.minDate ?? data),
        maxDate: transformDate(data.maxDate ?? data),
      }[code] ?? data;

    return {
      code,
      data: { ...data, value },
    };
  }

  private isError(error: OnyxErrorType): error is OnyxError {
    return 'code' in error;
  }
}
